import {
  PRE_CREATED_ENGAGEMENT_ID,
  RECEIVER_ADDRESS1,
  RECEIVER_ADDRESS2,
  RECEIVER_ADDRESS_BOOK,
  RECEIVER_CITY,
  RECEIVER_COMPANY_NAME,
  RECEIVER_COUNTRY,
  RECEIVER_EMAIL,
  RECEIVER_FIRST_NAME,
  RECEIVER_FIXED_ADDRESS,
  RECEIVER_LAST_NAME,
  RECEIVER_PHONE,
  RECEIVER_STATE,
  RECEIVER_TAX_NUMBER,
  RECEIVER_ZIP,
} from '../constants/shipping';
import { ImageFitEnum, ImagePositionEnum } from './shell';

export interface IOrgInfo {
  org_id?: string;
  name?: string;
  logo_url: string;
  header_color: string;
  button_color: string;
  vector_image_url: string;
}

export type TInitAddress = {
  [RECEIVER_EMAIL]: string;
  [RECEIVER_FIRST_NAME]: string;
  [RECEIVER_LAST_NAME]: string;
  [RECEIVER_PHONE]: string;
  [RECEIVER_TAX_NUMBER]?: string;
} & (
  | {
      [RECEIVER_ADDRESS1]: string;
      [RECEIVER_ADDRESS2]?: string;
      [RECEIVER_CITY]: string;
      [RECEIVER_COMPANY_NAME]?: string;
      [RECEIVER_COUNTRY]: string;
      [RECEIVER_STATE]?: string;
      [RECEIVER_ZIP]: string;
      [RECEIVER_FIXED_ADDRESS]?: never;
      [RECEIVER_ADDRESS_BOOK]?: IFixedAddress[];
    }
  | {
      [RECEIVER_ADDRESS1]?: never;
      [RECEIVER_ADDRESS2]?: never;
      [RECEIVER_CITY]?: never;
      [RECEIVER_COMPANY_NAME]?: never;
      [RECEIVER_COUNTRY]?: never;
      [RECEIVER_STATE]?: never;
      [RECEIVER_ZIP]?: never;
      [RECEIVER_ADDRESS_BOOK]?: never;
      [RECEIVER_FIXED_ADDRESS]: IFixedAddress[];
    }
);

export interface IAddressInfo
  extends Pick<
    TInitAddress,
    | 'receiver_address1'
    | 'receiver_address2'
    | 'receiver_city'
    | 'receiver_country'
    | 'receiver_zip'
    | 'receiver_state'
    | 'receiver_company_name'
  > {}

export interface IContactInfo
  extends Pick<
    TInitAddress,
    'receiver_first_name' | 'receiver_last_name' | 'receiver_email' | 'receiver_phone' | 'receiver_tax_number'
  > {}

export type IReceiverInfo = IAddressInfo | IContactInfo;

export interface IPickedMskuItem {
  parent_id: string;
  uid: string;
  quantity: number;
  count?: number;
}

export interface IFixedAddress {
  address1: string;
  address2?: string;
  city: string;
  country: string;
  company_name?: string;
  state?: string;
  zip: string;
  label?: string;
  uid?: string;
}

export interface ICustomizableStoredItem {
  internalId: string;
  item_id: string;
  item_customization_text: string;
}

export interface ICustomizableItem extends Omit<ICustomizableStoredItem, 'internalId'> {}

export interface IPYGInformation {
  [PRE_CREATED_ENGAGEMENT_ID]?: string;
}

export interface IMskuInformation {
  picked_items?: { [id: string]: IPickedMskuItem | null };
}

export interface ICustomItemsInformation {
  custom_items?: { [id: string]: ICustomizableItem | null };
}

export interface ISenderInfo {
  sender_company_name: string;
  sender_first_name: string;
  sender_last_name: string;
  sender_uid: string;
}

export interface IWhiteLabeling {
  ds_header_color?: string;
  ds_button_color?: string;
  shipping_page_title: string;
  shipping_page_body: string;
  pick_your_gift_title: string;
  pick_your_gift_body: string;
  msku_page_title?: string;
  msku_page_body?: string;
  customize_items_page_title?: string;
  customize_items_page_body?: string;
  ds_shipping_page_title?: string;
  ds_shipping_page_body?: string;
  ds_msku_page_title: string;
  ds_msku_page_body: string;
  ds_customize_items_page_title: string;
  ds_customize_items_page_body: string;
  ds_company_name?: string;
  ds_logo_url?: string;
  ds_box_image_url?: string;
}

interface IExperienceTextStyle {
  bold?: boolean;
  italic?: boolean;
  color?: string;
}

interface IExperienceTypography {
  title?: IExperienceTextStyle;
  body?: IExperienceTextStyle;
}

interface IExperienceButtons {
  action?: { color?: string; radius?: number };
  stepper?: { color?: string };
}

interface IExperienceHeader {
  media?: { image_url?: string };
  app_bar?: {
    background_color?: string;
    logo_image_url?: string;
    logo_image_href?: string;
    logo_image_size?: number;
  };
  hero_section?: {
    background_color?: string;
    image_url?: string;
    image_position?: ImagePositionEnum | string;
    // image_fit also encodes the hero height mode: 'contain' = Adapt (hero tracks the image
    // aspect), 'cover' = Auto (content-driven height with a fixed floor). The renderer measures
    // the image aspect itself, so no aspect ratio is persisted.
    image_fit?: ImageFitEnum | string;
  };
}

export interface IExperiencePageOverride {
  background?: { color?: string };
  buttons?: IExperienceButtons;
  header?: IExperienceHeader;
  typography?: IExperienceTypography;
}

interface IExperiencePageContent {
  header_text?: string;
  body_text?: string;
  overrides?: IExperiencePageOverride;
}

export interface IExperience {
  cover_image_url?: string;
  header?: IExperienceHeader;
  buttons?: IExperienceButtons;
  background?: { color?: string };
  typography?: IExperienceTypography;
  pages?: Partial<Record<ExperiencePageKeyEnum, IExperiencePageContent>>;
}

export enum ExperiencePageKeyEnum {
  PickYourGift = 'pick_your_gift',
  PickYourOption = 'pick_your_option',
  ShippingPage = 'shipping_page',
}
